import { ZENDESK_AUTH_ENDPOINT } from '@/constants/endpoints';
import { useAppSelector } from '@/store/hooks';
import { selectIsZendeskWidgetEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

declare global {
  interface Window {
    zE?: (...args: unknown[]) => void;
  }
}

const ZENDESK_SCRIPT_ID = 'ze-snippet';

export default function ZendeskWidget() {
  const { userProfile } = useAppSelector(state => state.me);
  const isImpersonationMode = useSelector(
    ({ auth }: { auth: { isImpersonationMode: boolean } }) => auth.isImpersonationMode,
  );
  const isZendeskWidgetEnabled = useAppSelector(selectIsZendeskWidgetEnabled);
  const zendeskKey = import.meta.env.VITE_ZENDESK_KEY;
  const [zeReady, setZeReady] = useState(typeof window !== 'undefined' && !!window.zE);

  // Inject the Zendesk Messaging snippet only after the whitelabel config confirms it is enabled.
  useEffect(() => {
    if (!isZendeskWidgetEnabled || !zendeskKey) return;

    if (window.zE) {
      setZeReady(true);
      return;
    }

    let script = document.getElementById(ZENDESK_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = ZENDESK_SCRIPT_ID;
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`;
      script.async = true;
      document.head.appendChild(script);
    }

    const handleLoad = () => setZeReady(true);
    script.addEventListener('load', handleLoad);
    return () => {
      script?.removeEventListener('load', handleLoad);
    };
  }, [isZendeskWidgetEnabled, zendeskKey]);

  useEffect(() => {
    if (!isZendeskWidgetEnabled) return;
    if (!zeReady || !window.zE) return;

    try {
      if (isImpersonationMode) {
        window.zE('messenger', 'hide');
        window.zE('messenger', 'logoutUser');
        return;
      }

      window.zE('messenger', 'show');

      if (userProfile?.email) {
        window.zE('messenger', 'loginUser', async (callback: (jwt: string) => void) => {
          try {
            const { data } = await axios.get<{ jwt: string }>(ZENDESK_AUTH_ENDPOINT);
            callback(data.jwt);
          } catch (e) {
            console.warn('Zendesk auth token fetch failed:', e);
          }
        });
      }
    } catch (e) {
      console.warn('Zendesk widget error:', e);
    }
  }, [userProfile?.email, isImpersonationMode, isZendeskWidgetEnabled, zeReady]);

  return null;
}
